/**
 * PUNKT-SPRUNG — Hilfen für VisualStoryStore.springeZuSlide, den Sprung über
 * die Kapitel-Punkte (Jan 06.10.2026: im Tempo der Timeline, abbrechbar
 * durch Scrollen).
 */

/**
 * Gleichmäßiges Tempo für Läufe über mehrere Slides: kurz anfahren und
 * abbremsen (je `rampeMs`), dazwischen konstant. quadInOut über 20 s hieße:
 * sekundenlang kaum Bewegung nach dem Klick, in der Mitte doppeltes
 * Timeline-Tempo. Bei Dauern bis 2 × rampeMs ist es genau quadInOut.
 */
export function gleichmaessig(dauerMs: number, rampeMs = 600): (t: number) => number {
    const r = Math.min(0.5, rampeMs / dauerMs);
    const spitze = 1 / (1 - r);
    return (t) => {
        if (t <= 0) return 0;
        if (t >= 1) return 1;
        if (t < r) return (spitze * t * t) / (2 * r);
        if (t > 1 - r) return 1 - (spitze * (1 - t) * (1 - t)) / (2 * r);
        return spitze * (t - r / 2);
    };
}

/**
 * Absichtliches Scrollen von Trackpad-Nachschwung unterscheiden — nur
 * Absicht bricht einen Punkt-Sprung ab.
 *
 * Nach dem Loslassen schickt macOS noch bis zu ~1,5 s Rad-Ereignisse mit
 * abklingenden Ausschlägen. Wer kurz scrollt und dann auf einen Punkt
 * klickt, bräche den Sprung sonst sofort selbst wieder ab. Als Nachschwung
 * gilt: alles in der Schonfrist nach dem Start, danach jedes Ereignis, das
 * dicht (< 100 ms) auf ein gleich großes oder größeres folgt. Ein neues
 * Rad-Rasten nach einer Pause oder ein wieder anschwellender Ausschlag ist
 * Absicht.
 *
 * @returns Prüfung je Rad-Ereignis: true = absichtlich gescrollt
 */
export function radAbsicht(start: number, schonfrist = 400): (jetzt: number, deltaY: number) => boolean {
    let letzteZeit = -Infinity;
    let letzterAusschlag = Infinity;
    return (jetzt, deltaY) => {
        const ausschlag = Math.abs(deltaY);
        const nachschwung = jetzt - start < schonfrist || (jetzt - letzteZeit < 100 && ausschlag <= letzterAusschlag);
        letzteZeit = jetzt;
        letzterAusschlag = ausschlag;
        return !nachschwung;
    };
}
